import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  MapPin,
  Phone,
  Mail,
  Car,
  Navigation,
  Clock,
  Loader2,
} from "lucide-react";
import { statusClass } from "./AdminDashboard";
import RouteMap from "@/components/RouteMap";
import OnMyWayButton from "@/components/OnMyWayButton";

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function startOfWeekISO(iso) {
  const d = new Date(iso + "T00:00:00");
  // ISO week starts Monday
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminCalendar() {
  const [weekStart, setWeekStart] = useState(startOfWeekISO(todayISO()));
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [selectedDay, setSelectedDay] = useState(todayISO());

  const dayList = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const weekEnd = dayList[6];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/calendar", {
        params: { start: weekStart, end: weekEnd },
      });
      setDays(data.days || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [weekStart]);

  const onDrop = async (toDate) => {
    if (!dragging || dragging.scheduled_date === toDate) {
      setDragging(null);
      return;
    }
    try {
      await api.patch(`/bookings/${dragging.id}/reschedule`, {
        scheduled_date: toDate,
        scheduled_time: dragging.scheduled_time,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Reschedule failed");
    } finally {
      setDragging(null);
    }
  };

  const [route, setRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    if (!selectedDay) return;
    setRouteLoading(true);
    api
      .get("/route/day", { params: { date: selectedDay } })
      .then((r) => setRoute(r.data))
      .catch(() => setRoute(null))
      .finally(() => setRouteLoading(false));
  }, [selectedDay, days]);

  const routeStops = route?.stops || [];
  const routeMapUrl = route?.google_maps_url || null;

  return (
    <div data-testid="admin-calendar">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Schedule</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">
            Weekly calendar
          </h1>
          <div className="font-mono text-xs text-slate-500 mt-1">
            {fmtDay(weekStart)} — {fmtDay(weekEnd)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="calendar-prev-week"
            className="cj-btn-ghost px-3 py-2 rounded-lg inline-flex items-center gap-1 text-xs"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button
            onClick={() => setWeekStart(startOfWeekISO(todayISO()))}
            data-testid="calendar-today-button"
            className="cj-btn-ghost px-3 py-2 rounded-lg text-xs"
          >
            Today
          </button>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="calendar-next-week"
            className="cj-btn-ghost px-3 py-2 rounded-lg inline-flex items-center gap-1 text-xs"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="calendar-grid">
        {dayList.map((d) => {
          const dayBookings = (days[d] || []).slice().sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
          const isToday = d === todayISO();
          const isSelected = d === selectedDay;
          return (
            <div
              key={d}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(d)}
              onClick={() => setSelectedDay(d)}
              className={`cj-card p-3 min-h-[180px] cursor-pointer transition ${
                isSelected ? "border-[#ff6b00]/60" : ""
              }`}
              data-testid={`calendar-day-${d}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {fmtDay(d).split(" ")[0]}
                </div>
                {isToday && (
                  <span className="cj-pill bg-[#ff6b00] text-[#0b0f17] border border-[#ff6b00]">Today</span>
                )}
              </div>
              <div className="font-display font-extrabold text-2xl text-slate-100 mt-1">
                {fmtDay(d).split(" ")[1]}
              </div>
              <div className="font-mono text-[10px] uppercase text-slate-500 mt-0.5">
                {fmtDay(d).split(" ")[2]}
              </div>
              <div className="mt-3 space-y-2">
                {dayBookings.length === 0 && (
                  <div className="text-xs text-slate-600 font-mono">No bookings</div>
                )}
                {dayBookings.map((b) => (
                  <div
                    key={b.id}
                    draggable
                    onDragStart={() => setDragging(b)}
                    onDragEnd={() => setDragging(null)}
                    className="p-2 rounded-lg bg-[#0f1522] border border-white/10 hover:border-[#ff6b00]/50 cursor-grab active:cursor-grabbing"
                    data-testid={`calendar-booking-${b.id}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono text-[10px] text-[#ff6b00]">{b.scheduled_time}</span>
                      <span className={`cj-pill text-[9px] border ${statusClass(b.status)}`}>
                        {b.status.replace("_", " ")}
                      </span>
                    </div>
                    <div className="font-display uppercase font-bold text-xs text-slate-100 mt-1 truncate">
                      {b.customer.name}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500 truncate">
                      {b.vehicle.make} {b.vehicle.model}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Daily route · auto-planned</div>
        <h2 className="font-display mt-2 text-2xl sm:text-3xl font-extrabold uppercase text-slate-100">
          {fmtDay(selectedDay)} · {routeStops.length} stop{routeStops.length !== 1 && "s"}
          {route && routeStops.length > 0 && <span className="font-mono text-base text-slate-400 ml-3">≈ {route.total_km} km round trip</span>}
        </h2>
        {route?.order_differs && (
          <div className="mt-3 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-200 inline-block" data-testid="route-optimized-notice">
            Optimised order differs from booking times — drives ≈ {route.saves_km} km less. Drag cards above to re-time bookings if you want to follow it.
          </div>
        )}

        {routeLoading && <div className="mt-4 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Planning route…</div>}
        {route && routeStops.length > 0 && (
          <div className="mt-5">
            <RouteMap home={route.home} stops={routeStops} />
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          {routeMapUrl && (
            <a
              href={routeMapUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="route-google-maps-button"
              className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2"
            >
              <Navigation className="h-4 w-4" /> Open optimised route in Google Maps
            </a>
          )}
        </div>

        <div className="mt-5 grid gap-3">
          {routeStops.length === 0 && !routeLoading && (
            <div className="cj-card p-6 text-slate-400 text-sm">No bookings on this day.</div>
          )}
          {routeStops.map((b, i) => (
            <div key={b.id} className="cj-card p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`route-stop-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-[#ff6b00]/10 flex items-center justify-center shrink-0">
                  <span className="font-display font-extrabold text-[#ff6b00] text-sm">{b.order}</span>
                </div>
                <div className="min-w-0">
                  <div className="font-display uppercase font-bold text-slate-100 truncate">
                    <Clock className="h-3.5 w-3.5 inline mr-1 text-[#ff6b00]" />
                    <span className="font-mono text-[#ff6b00] text-sm mr-2">{b.scheduled_time}</span>
                    {b.customer_name}
                  </div>
                  <div className="font-mono text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1">
                      <Car className="h-3 w-3" /> {b.vehicle}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {b.address}
                    </span>
                    {b.leg_km != null && <span className="text-cyan-300">+{b.leg_km} km</span>}
                  </div>
                  <div className="font-mono text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4">
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {b.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {b.email}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`cj-pill border ${statusClass(b.status)}`}>{b.status.replace("_", " ")}</span>
                <span className="font-display font-extrabold text-[#ff6b00] text-sm">{b.service_name}</span>
                {!["completed", "cancelled", "waitlisted", "declined"].includes(b.status) && (
                  <OnMyWayButton bookingId={b.id} fromBookingId={i > 0 ? routeStops[i - 1].id : null} customerName={b.customer_name} compact alreadyAt={b.on_my_way_at} />
                )}
              </div>
            </div>
          ))}
        </div>

        {!loading && Object.keys(days).length === 0 && (
          <div className="mt-4 text-xs text-slate-500 font-mono">
            Tip: drag any booking card between day columns to reschedule.
          </div>
        )}
      </div>
    </div>
  );
}
